//@flow
import * as Immutable from 'immutable';

import { MasterLineItem } from 'common/models/flow/MasterLineItem';
import type { MasterLineItemModelType } from 'common/models/flow/MasterLineItem';

import { LocationId } from './LocationId';
import { TemperatureThreshold } from './TemperatureThreshold';
import { Carton } from './Carton';
import { VolumeUnitType } from 'common/models/internal/Volume';
import { SkillId } from './SkillId';
import DataUtils from 'common/utils/DataUtils';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { AuditMetadata } from './AuditMetadata';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import RegExUtil from 'common/utils/RegExUtil';
import StringUtils from 'common/utils/StringUtils';
import NumberUtils from 'common/utils/NumberUtils';

import I18nHelper from 'common/helpers/I18nHelper';

import { LineItemPart } from './LineItemPart';

import { MasterLocation } from './MasterLocation';
import { ENTITY_NAME_ID } from 'common/constants/entity/lineItemMaster';

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalMasterLineItem extends MasterLineItem {
    static fromJS(json: MasterLineItemModelType): InternalMasterLineItem {
        const state: Object = Object.assign({}, json);
        state.parts = Immutable.List(state.parts).map(item =>
            LineItemPart.fromJS(item)
        );
        state.locationIds = Immutable.List(state.locationIds).map(item =>
            LocationId.fromJS(item)
        );
        state.locations = Immutable.List(state.locations).map(item =>
            MasterLocation.fromJS(item)
        );
        state.instructions = Immutable.List(state.instructions);
        state.temperatureThreshold = state.temperatureThreshold
            ? TemperatureThreshold.fromJS(state.temperatureThreshold)
            : state.temperatureThreshold;
        state.carton = state.carton
            ? Carton.fromJS(state.carton)
            : state.carton;
        state.customProperties = Immutable.Map(state.customProperties);
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    isSame(lineItem: MasterLocation): boolean {
        return this.clientId === lineItem.clientId && this.id === lineItem.id;
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            StringUtils.isEmpty(this.name) ||
            StringUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setScanId(scanId: ?string): this {
        return super.setScanId(scanId);
    }

    // scanId is optional field but if provided should be alphanumeric only
    validateScanId() {
        const { scanId } = this;
        if (scanId && scanId.length > 64) {
            return nsTranslate('skuValidationErrors.scanIdPattern');
        }
    }

    @validate
    setTransactionDuration(transactionDuration: ?number): this {
        return super.setTransactionDuration(transactionDuration);
    }

    validateTransactionDuration() {
        if (!this.transactionDuration) {
            return;
        }
        if (!NumberUtils.isNonNegativeNumber(this.transactionDuration)) {
            return nsTranslate('skuValidationErrors.tatNotANumber');
        }
    }

    @softValidateAll
    hasValidationErrors({ helpers }): boolean {
        let hasErrors = false;
        const { temperatureThreshold, parts, carton } = this;
        if (temperatureThreshold) {
            hasErrors = hasErrors || temperatureThreshold.hasValidationErrors();
        }
        parts.forEach(part => {
            hasErrors = hasErrors || part.hasValidationErrors();
        });
        if (carton) {
            hasErrors =
                hasErrors ||
                carton.hasValidationErrors({
                    lineItem: this,
                    unitConverter: helpers?.unitConverter
                });
        }
        if (
            !DataUtils.isEmpty(this.shouldCrate) &&
            typeof this.shouldCrate !== 'boolean'
        ) {
            hasErrors = true;
        }
        return hasErrors;
    }

    @validateAll
    setAllValidationErrors(): this {
        var self = this;
        const { temperatureThreshold, parts, carton } = this;
        if (temperatureThreshold) {
            self = self.setTemperatureThreshold(
                temperatureThreshold.setAllValidationErrors()
            );
        }
        self = self.setParts(
            parts.map(part => {
                return part.setAllValidationErrors();
            })
        );
        if (carton) {
            self = self.setCarton(carton.setAllValidationErrors());
        }
        return self;
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    getCategoryValidationMessage({ salesCategories }) {
        let validationMessage = null;
        const salesCategoryNames = salesCategories.map(
            salesCategory => salesCategory.value
        );
        if (
            this.category &&
            this.category.trim() &&
            !salesCategoryNames.includes(this.category)
        ) {
            validationMessage = nsTranslate(
                'lineItemValidationErrors.invalidSalesCategory',
                {
                    salesCategoryNames: salesCategoryNames.join(', ')
                }
            );
        }
        return validationMessage;
    }

    getTotalPartsVolume(unitConverter: UnitConversionHelper) {
        let partsVolume = 0;
        this.parts.forEach(part => {
            if (!part.hasValidationErrors()) {
                partsVolume =
                    partsVolume +
                    NumberUtils.toNumber(part.getTotalVolume(unitConverter));
            }
        });
        return partsVolume;
    }

    getTotalPartsWeight() {
        let partsWeight = 0;
        this.parts.forEach(part => {
            if (!part.hasValidationErrors() && part.weight) {
                partsWeight = partsWeight + NumberUtils.toNumber(part.weight);
            }
        });
        return partsWeight;
    }

    sanitize(oldEntity, helpers): this {
        let updatedLineItem = this;
        const { locations, instructions } = updatedLineItem;

        // Setting LocationIds from locations as we need to send locationIds to server
        updatedLineItem = updatedLineItem.setLocationIds(
            Immutable.List(
                locations.map(location => location.extractLocationId())
            )
        );

        //Reset locations to [] or to what defaultLineItem was, because server does not care about it.
        updatedLineItem = updatedLineItem.setLocations(
            oldEntity ? oldEntity.locations : Immutable.List([])
        );

        // convert stuff to source units
        const { parts } = updatedLineItem;
        const updatedParts = parts.map(part => {
            let updatedPart = part;
            if (part.dimensions?.isEmpty()) {
                updatedPart = part
                    .setDimensions(null)
                    .defaultSanitize(part, helpers?.unitConverter);
            } else {
                updatedPart = part.defaultSanitize(
                    part,
                    helpers?.unitConverter
                );
            }
            return updatedPart;
        });
        updatedLineItem = updatedLineItem.setParts(updatedParts);

        const { temperatureThreshold } = updatedLineItem;
        if (temperatureThreshold) {
            updatedLineItem = updatedLineItem.setTemperatureThreshold(
                temperatureThreshold.convertToSourceUnit(helpers?.unitConverter)
            );
        }

        const { carton } = updatedLineItem;
        if (carton) {
            updatedLineItem = updatedLineItem.setCarton(
                carton.convertToSourceUnit(helpers?.unitConverter)
            );
        }

        if (instructions) {
            updatedLineItem = updatedLineItem.setInstructions(
                instructions.filter(
                    instruction => !DataUtils.isEmpty(instruction)
                )
            );
        }

        return updatedLineItem;
    }

    get price(): ?Amount {
        return this._state.get('price');
    }

    get dimensions(): ?Dimensions {
        return this._state.get('dimensions');
    }

    get manufacturingLocation(): ?LocationId {
        return this._state.get('manufacturingLocation');
    }

    get temperatureThreshold(): ?TemperatureThreshold {
        return this._state.get('temperatureThreshold');
    }

    get carton(): ?Carton {
        return this._state.get('carton');
    }

    get volumes(): Immutable.List<Volume> {
        return this._state.get('volumes');
    }

    get parts(): Immutable.List<LineItemPart> {
        return this._state.get('parts');
    }

    get locationIds(): Immutable.List<LocationId> {
        return this._state.get('locationIds');
    }

    get locations(): Immutable.List<MasterLocation> {
        return this._state.get('locations');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(oldEntity, helpers): ?this {
        let sanitizedLineItem = this.sanitize(oldEntity, helpers);
        sanitizedLineItem = super.defaultSanitize(sanitizedLineItem);

        if (sanitizedLineItem) {
            const sanitizedTemperatureThreshold = sanitizedLineItem.temperatureThreshold?.defaultSanitize();
            return sanitizedLineItem.setTemperatureThreshold(
                sanitizedTemperatureThreshold
            );
        }
        return sanitizedLineItem;
    }

    static defaultInit({ props }): InternalMasterLineItem {
        const temperatureThreshold = TemperatureThreshold.defaultInit({
            props
        });

        return InternalMasterLineItem.fromJS({
            parts: [
                {
                    volume: {
                        unit: VolumeUnitType.MetersCubic,
                        value: null
                    },
                    weight: null
                }
            ]
        }).setTemperatureThreshold(temperatureThreshold);
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }
}

export { InternalMasterLineItem as MasterLineItem };
